/**
 * Timezone-safe date utilities for booking applications
 *
 * These functions ensure that date operations are not affected by timezone differences.
 * All dates are treated as calendar dates (not timestamps) to avoid timezone issues.
 */

/**
 * Parse a date string safely without timezone conversion
 * @param {string|Date} dateInput - Date string (YYYY-MM-DD) or Date object
 * @returns {Date} Date object representing the calendar date in local timezone
 */
export function parseDate(dateInput) {
  if (!dateInput) return null;

  // If already a Date object, return it
  if (dateInput instanceof Date) {
    return dateInput;
  }

  // For string dates, parse as local date to avoid timezone issues
  if (typeof dateInput === 'string') {
    // Handle YYYY-MM-DD format
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
      const [year, month, day] = dateInput.split('-').map(Number);
      return new Date(year, month - 1, day); // month is 0-indexed
    }

    // Handle DD-MM-YYYY format
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateInput)) {
      const [day, month, year] = dateInput.split('-').map(Number);
      return new Date(year, month - 1, day); // month is 0-indexed
    }

    // Fallback to standard Date parsing
    return new Date(dateInput);
  }

  return null;
}

/**
 * Format a date as YYYY-MM-DD string (timezone-safe)
 * @param {Date|string} dateInput - Date to format
 * @returns {string} Date in YYYY-MM-DD format
 */
export function formatDateISO(dateInput) {
  const date = parseDate(dateInput);
  if (!date) return '';

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

/**
 * Format a date as DD-MM-YYYY string (timezone-safe)
 * @param {Date|string} dateInput - Date to format
 * @returns {string} Date in DD-MM-YYYY format
 */
export function formatDateDDMMYYYY(dateInput) {
  const date = parseDate(dateInput);
  if (!date) return '';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
}

/**
 * Calculate the number of days between two dates (inclusive)
 * @param {Date|string} startDate - Start date
 * @param {Date|string} endDate - End date
 * @returns {number} Number of days between dates (inclusive)
 */
export function calculateDaysInclusive(startDate, endDate) {
  const start = parseDate(startDate);
  const end = parseDate(endDate);

  if (!start || !end) return 0;

  // Create clean date objects without time components
  const startDateOnly = new Date(
    start.getFullYear(),
    start.getMonth(),
    start.getDate(),
  );
  const endDateOnly = new Date(
    end.getFullYear(),
    end.getMonth(),
    end.getDate(),
  );

  let days = 0;
  const currentDate = new Date(startDateOnly);
  while (currentDate <= endDateOnly) {
    days++;
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return days;
}

/**
 * Calculate the number of nights between two dates (exclusive of end date)
 * @param {Date|string} startDate - Start date
 * @param {Date|string} endDate - End date
 * @returns {number} Number of nights between dates
 */
export function calculateNights(startDate, endDate) {
  const start = parseDate(startDate);
  const end = parseDate(endDate);

  if (!start || !end) return 0;

  // Create clean date objects without time components
  const startDateOnly = new Date(
    start.getFullYear(),
    start.getMonth(),
    start.getDate(),
  );
  const endDateOnly = new Date(
    end.getFullYear(),
    end.getMonth(),
    end.getDate(),
  );

  let nights = 0;
  const currentDate = new Date(startDateOnly);
  while (currentDate < endDateOnly) {
    nights++;
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return nights;
}

/**
 * Add days to a date (timezone-safe)
 * @param {Date|string} dateInput - Base date
 * @param {number} days - Number of days to add
 * @returns {Date} New date with days added
 */
export function addDays(dateInput, days) {
  const date = parseDate(dateInput);
  if (!date) return null;

  const result = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  result.setDate(result.getDate() + days);
  return result;
}

/**
 * Get today's date as YYYY-MM-DD string (timezone-safe)
 * @returns {string} Today's date in YYYY-MM-DD format
 */
export function getTodayISO() {
  const today = new Date();
  return formatDateISO(today);
}

/**
 * Check if a date is today (timezone-safe)
 * @param {Date|string} dateInput - Date to check
 * @returns {boolean} True if the date is today
 */
export function isToday(dateInput) {
  const date = parseDate(dateInput);
  if (!date) return false;

  const today = new Date();
  return (
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate()
  );
}

/**
 * Compare two dates (timezone-safe)
 * @param {Date|string} date1 - First date
 * @param {Date|string} date2 - Second date
 * @returns {number} -1 if date1 < date2, 0 if equal, 1 if date1 > date2
 */
export function compareDates(date1, date2) {
  const d1 = parseDate(date1);
  const d2 = parseDate(date2);

  if (!d1 || !d2) return 0;

  // Create clean date objects for comparison
  const clean1 = new Date(d1.getFullYear(), d1.getMonth(), d1.getDate());
  const clean2 = new Date(d2.getFullYear(), d2.getMonth(), d2.getDate());

  if (clean1 < clean2) return -1;
  if (clean1 > clean2) return 1;
  return 0;
}
